import { Box, Grid, Paper, Skeleton, Stack } from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { Suspense } from "react";

import { SubmissionGeneratorStatusContent } from "./SubmissionGeneratorStatusTile";
import { SubmissionStateContent } from "./SubmissionStateTile";
import { SubmissionStatusContent } from "./SubmissionStatusTile";
import { useWasteSubmissionsOverviewContext } from "./WasteSubmissionsOverviewContext";

type SubmissionStatsOverviewProps = {
  onStatusClick?: (status: SubmissionStatus) => void;
  selectedStatus?: SubmissionStatus | null;
  onStateClick?: (state: string) => void;
  selectedState?: string | null;
  onGeneratorStatusClick?: (status: string) => void;
  selectedGeneratorStatus?: string | null;
};

// Skeleton that mimics donut chart with legend on right
// Matches DonutChart with containerHeight="250px" and right legend
function DonutChartSkeleton() {
  return (
    <>
      {/* Header skeleton - matches DashboardTile.Header */}
      <Skeleton variant="text" width={140} height={32} />
      {/* Chart content area - matches DashboardTile.ContentArea with DonutChart */}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        gap={2}
        sx={{ height: 250, minHeight: 250 }}
      >
        {/* Donut skeleton - circular shape matching 180px inner size */}
        <Skeleton variant="circular" width={180} height={180} sx={{ flexShrink: 0 }} />
        {/* Legend items on the right */}
        <Stack spacing={1.5} sx={{ minWidth: 120 }}>
          <Skeleton variant="rounded" width={120} height={20} />
          <Skeleton variant="rounded" width={100} height={20} />
          <Skeleton variant="rounded" width={110} height={20} />
          <Skeleton variant="rounded" width={85} height={20} />
        </Stack>
      </Box>
    </>
  );
}

// Skeleton that mimics horizontal bar chart
// Matches SubmissionStateContent with 5 horizontal bars at 28px height each
function BarChartSkeleton() {
  return (
    <>
      {/* Header skeleton - matches DashboardTile.Header */}
      <Skeleton variant="text" width={80} height={32} />
      {/* Bar chart content area - matches DashboardTile.ContentArea */}
      <Stack spacing={1.5} sx={{ py: 0.5, minHeight: 250 }}>
        {[100, 75, 60, 40, 25].map((width, index) => (
          <Stack key={index} direction="row" alignItems="center" spacing={1}>
            {/* State label skeleton */}
            <Skeleton variant="text" width={28} height={28} sx={{ flexShrink: 0 }} />
            {/* Bar skeleton */}
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="rounded" width={`${width}%`} height={28} />
            </Box>
            {/* Count skeleton */}
            <Skeleton variant="text" width={36} height={28} sx={{ flexShrink: 0 }} />
          </Stack>
        ))}
      </Stack>
    </>
  );
}

function StatsSkeleton() {
  return (
    <Paper elevation={0}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <DonutChartSkeleton />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <DonutChartSkeleton />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <BarChartSkeleton />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

// Note: WasteSubmissionsOverviewProvider is now rendered at the top level (in WasteSubmissions)
// so chart queries fire BEFORE the DataGrid starts rendering.
// This component just consumes the context - no provider wrapper needed here.
export function SubmissionStatsOverview({
  onStatusClick,
  selectedStatus,
  onStateClick,
  selectedState,
  onGeneratorStatusClick,
  selectedGeneratorStatus,
}: SubmissionStatsOverviewProps) {
  return (
    <Suspense fallback={<StatsSkeleton />}>
      <SubmissionStatsOverviewContent
        onStatusClick={onStatusClick}
        selectedStatus={selectedStatus}
        onStateClick={onStateClick}
        selectedState={selectedState}
        onGeneratorStatusClick={onGeneratorStatusClick}
        selectedGeneratorStatus={selectedGeneratorStatus}
      />
    </Suspense>
  );
}

// Inner component that consumes context and applies opacity during transitions
function SubmissionStatsOverviewContent({
  onStatusClick,
  selectedStatus,
  onStateClick,
  selectedState,
  onGeneratorStatusClick,
  selectedGeneratorStatus,
}: SubmissionStatsOverviewProps) {
  const { isPending } = useWasteSubmissionsOverviewContext();

  return (
    <Paper elevation={0} sx={{ opacity: isPending ? 0.5 : 1, transition: "opacity 0.2s" }}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Status donut, Generator Status donut, State bar chart */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <SubmissionStatusContent
                onStatusClick={onStatusClick}
                selectedStatus={selectedStatus}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <SubmissionGeneratorStatusContent
                onGeneratorStatusClick={onGeneratorStatusClick}
                selectedGeneratorStatus={selectedGeneratorStatus}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <SubmissionStateContent onStateClick={onStateClick} selectedState={selectedState} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export { SubmissionGeneratorStatusTile } from "./SubmissionGeneratorStatusTile";
export { SubmissionStateTile } from "./SubmissionStateTile";
export { SubmissionStatusTile } from "./SubmissionStatusTile";
